// The support assistant (spec §11): POST /support/ask streams Claude's answer as newline-delimited JSON -
// {"text": "..."} fragments, then {"done": true}, or {"error": "failed" | "declined"}. The API holds the key and
// grounds the answer in docs/Xpedition2_Web_Func_Spec.md.
import { apiFetch } from '@/api/client';

export interface SupportMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** Why there's no (complete) answer: the service failed or isn't set up, or the question was declined. */
export class SupportError extends Error {
  constructor(readonly reason: 'failed' | 'declined' | 'unavailable') {
    super(`Support ${reason}`);
  }
}

/**
 * Asks about the last (user) message of the conversation; `onText` gets each fragment of the answer as it
 * arrives. Resolves when the answer is complete.
 */
export async function askSupport(
  conversation: SupportMessage[],
  onText: (text: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await apiFetch('/support/ask', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages: conversation }),
    signal,
  });
  if (response.status === 503) throw new SupportError('unavailable');
  if (!response.ok || !response.body) throw new SupportError('failed');

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    // Whole lines only: the last piece may be half a line.
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      if (!line.trim()) continue;
      const event = JSON.parse(line) as { text?: string; done?: boolean; error?: 'failed' | 'declined' };
      if (event.text) onText(event.text);
      if (event.error) throw new SupportError(event.error);
      if (event.done) return;
    }
  }
  // The stream ended without saying it was done: cut off.
  throw new SupportError('failed');
}
